import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Restore current variant ID from data attribute (preserved across gallery replacement)
    if (this.dataset.currentVariantId) {
      this.#currentVariantId = this.dataset.currentVariantId;
      delete this.dataset.currentVariantId;
    }

    // Listen for slideshow scroll/select events to auto-switch variant
    this.#setupVariantSwitching(signal);
  }

  #controller = new AbortController();
  #currentVariantId = null;
  #variantChangeInProgress = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Sets up variant switching when scrolling through variant-grouped media.
   * @param {AbortSignal} signal - The abort signal for cleanup.
   */
  #setupVariantSwitching(signal) {
    const slideshow = this.refs.slideshow;
    if (!slideshow) return;

    // Initialize current variant from the first visible slide
    queueMicrotask(() => {
      const firstVariant = this.#getVariantIdFromSlide(slideshow.current);
      if (firstVariant) this.#currentVariantId = firstVariant;
    });

    slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
  }

  /**
   * Handles slideshow selection events for auto-variant switching.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (this.#variantChangeInProgress) return;

    const { index, trigger } = event.detail;

    // Only auto-switch on scroll/drag, not programmatic selects
    if (trigger !== 'scroll' && trigger !== 'drag') return;

    const newVariantId = this.#getVariantIdFromSlide(index);
    if (!newVariantId || newVariantId === this.#currentVariantId) return;

    this.#currentVariantId = newVariantId;
    this.#selectVariant(newVariantId);
  };

  /**
   * Gets the variant ID from a slide at the given index.
   * @param {number} index - The slide index.
   * @returns {string|null} The variant ID or null.
   */
  #getVariantIdFromSlide(index) {
    const slides = this.slideshow?.slides;
    if (!slides || index < 0 || index >= slides.length) return null;

    const slide = slides[index];
    return slide?.getAttribute('data-variant-id') || null;
  }

  /**
   * Selects a variant in the variant picker.
   * @param {string} variantId - The variant ID to select.
   */
  #selectVariant(variantId) {
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;

    const variantPicker = section.querySelector('variant-picker');
    if (!variantPicker) return;

    // Find the radio/option with this variant ID
    const variantOption = variantPicker.querySelector(
      `input[data-variant-id="${variantId}"], option[data-variant-id="${variantId}"]`
    );

    if (!variantOption) return;

    // Check if it's a radio button (already checked)
    if (variantOption instanceof HTMLInputElement && variantOption.checked) return;

    this.#variantChangeInProgress = true;

    try {
      if (variantOption instanceof HTMLInputElement) {
        variantOption.click();
      } else if (variantOption instanceof HTMLOptionElement) {
        variantOption.selected = true;
        variantOption.closest('select')?.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } catch (e) {
      console.warn('Failed to auto-switch variant:', e);
    }

    // Reset the flag after variant update completes
    const handleUpdateComplete = () => {
      this.#variantChangeInProgress = false;
      section.removeEventListener(ThemeEvents.variantUpdate, handleUpdateComplete);
    };
    section.addEventListener(ThemeEvents.variantUpdate, handleUpdateComplete, { once: true });

    // Fallback: reset after timeout if variant update doesn't fire
    setTimeout(() => {
      this.#variantChangeInProgress = false;
    }, 3000);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Preserve the current variant ID across gallery replacement via data attribute
    if (this.#currentVariantId) {
      newMediaGallery.dataset.currentVariantId = this.#currentVariantId;
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
